import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TagButton } from "../../../core/components/input/tagSelection/TagButton";
import { H4 } from "../../../core/styles/stringStyles";
import { useStyles } from "../../../core/styles/theme";
import { hexWithOpacity } from "../../../core/utils/helpers";
import type {
  AppointmentDayKey,
  AppointmentSlotKey,
  PatientTimeslotTableModel,
} from "../utils/appointmentTimeslotUtils";

interface Props {
  tableModel: PatientTimeslotTableModel;
  selectedSlots: string[];
  onSelectionChange: (slots: string[]) => void;
}

export const TimeSlotTable: React.FC<Props> = ({
  tableModel,
  selectedSlots,
  onSelectionChange,
}) => {
  const theme = useStyles();
  const { t } = useTranslation();
  const { rows, columnKeysInOrder } = tableModel;

  const handleSlotClick = (
    dayKey: AppointmentDayKey,
    timeKey: AppointmentSlotKey,
  ) => {
    const slotValue = `${dayKey}_${timeKey}`;
    const newSelection = selectedSlots.includes(slotValue)
      ? selectedSlots.filter((slot) => slot !== slotValue)
      : [...selectedSlots, slotValue];

    onSelectionChange(newSelection);
  };

  const handleSlotClickAll = (timeKey: AppointmentSlotKey) => {
    const allSlotsForTime = rows
      .filter((row) => row.segments.some((s) => s.key === timeKey))
      .map((row) => `${row.dayKey}_${timeKey}`);
    if (allSlotsForTime.length === 0) return;

    const allSelected = allSlotsForTime.every((slot) =>
      selectedSlots.includes(slot),
    );
    const newSelection = allSelected
      ? selectedSlots.filter((slot) => !allSlotsForTime.includes(slot))
      : [
          ...selectedSlots,
          ...allSlotsForTime.filter((slot) => !selectedSlots.includes(slot)),
        ];

    onSelectionChange(newSelection);
  };

  return (
    <TableContainer>
      <TableBody>
        {rows.map((row) => (
          <BodyRow key={row.dayKey}>
            <DayText>{row.dayLabel}</DayText>
            <TimeSlotsContainer>
              {columnKeysInOrder.map((colKey) => {
                const seg = row.segments.find((s) => s.key === colKey);
                return (
                  <TimeSlotTags
                    key={`${row.dayKey}_${colKey}`}
                    selected={selectedSlots.includes(`${row.dayKey}_${colKey}`)}
                    onClick={
                      seg
                        ? () => handleSlotClick(row.dayKey, colKey)
                        : undefined
                    }
                    text={seg ? seg.displayText : ""}
                    bgColor={seg ? theme.colors.dark : "transparent"}
                    color={theme.colors.white}
                    inactiveColor={hexWithOpacity(theme.colors.dark, 0.5)}
                  />
                );
              })}
            </TimeSlotsContainer>
          </BodyRow>
        ))}
        <BodyRow>
          <DayText />
          <TimeSlotsContainer>
            {columnKeysInOrder.map((colKey) => (
              <TimeSlotTags
                key={`legend_${colKey}`}
                selected={false}
                text={t("TIME_SLOT_ALL")}
                onClick={() => handleSlotClickAll(colKey)}
                bgColor={theme.colors.dark}
                inactiveColor={hexWithOpacity(theme.colors.dark, 0.5)}
              />
            ))}
          </TimeSlotsContainer>
        </BodyRow>
      </TableBody>
    </TableContainer>
  );
};

const TableContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 10px;
`;

const TableBody = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BodyRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 15px;
`;

const TimeSlotsContainer = styled.div`
  display: flex;
  width: calc(100% - 24px);
  gap: 5px;
`;

const DayText = styled(H4)`
  width: 24px;
  color: ${(props) => hexWithOpacity(props.theme.colors.dark, 0.5)};
`;

const TimeSlotTags = styled(TagButton)`
  flex: 1;
  display: flex;
  justify-content: center;
  padding-left: 4px;
  padding-right: 4px;
`;
